import { useState } from 'react';
import {
  OUTCOME_OPTIONS, PIPELINE_STATUS_OPTIONS, SOURCE_OPTIONS
} from '../domain';
import type {
  Assignment, CallOutcome, NewBizProspect, NewBizSource, PipelineStatus, User
} from '../types';

interface ShellProps { children: React.ReactNode; onClose: () => void; maxWidth?: number }
function Shell({ children, onClose, maxWidth }: ShellProps) {
  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" style={maxWidth ? { maxWidth } : undefined} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

export interface PipelineTarget {
  kind: 'assign' | 'newbiz';
  id: string;
  companyName: string;
  status: PipelineStatus;
  outcome: CallOutcome | null;
  revenue: number;
  notes: string;
}

export function makePipelineTarget(item: Assignment | NewBizProspect, kind: 'assign' | 'newbiz'): PipelineTarget {
  if (kind === 'newbiz') {
    const n = item as NewBizProspect;
    return {
      kind, id: n.id, companyName: n.companyName,
      status: n.status, outcome: n.outcome, revenue: n.revenue, notes: n.notes
    };
  }
  const a = item as Assignment;
  return {
    kind, id: a.id, companyName: a.custName,
    status: a.status, outcome: a.outcome, revenue: a.revenue, notes: a.notes
  };
}

interface PipelineModalProps {
  target: PipelineTarget;
  onCancel: () => void;
  onSave: (patch: { status: PipelineStatus; outcome: CallOutcome | null; revenue: number; notes: string }) => void;
}

export function PipelineModal({ target, onCancel, onSave }: PipelineModalProps) {
  const [status, setStatus] = useState<PipelineStatus>(target.status);
  const [outcome, setOutcome] = useState<CallOutcome | ''>(target.outcome ?? '');
  const [revenue, setRevenue] = useState<string>(target.revenue ? String(target.revenue) : '');
  const [notes, setNotes] = useState<string>(target.notes ?? '');

  return (
    <Shell onClose={onCancel} maxWidth={480}>
      <div className="modal-title">Update <span>Account</span></div>
      <div className="modal-field">
        <label>Company</label>
        <input type="text" value={target.companyName} readOnly style={{ opacity: 0.65, cursor: 'default' }} />
      </div>
      <div className="modal-field">
        <label>Pipeline Status</label>
        <select value={status} onChange={(e) => setStatus(e.target.value as PipelineStatus)}>
          {PIPELINE_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      {status === 'called' && (
        <div className="modal-field">
          <label>Call Outcome</label>
          <select value={outcome} onChange={(e) => setOutcome(e.target.value as CallOutcome | '')}>
            <option value="">— Select outcome —</option>
            {OUTCOME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      )}
      {status === 'won' && (
        <div className="modal-field">
          <label>Revenue Generated (£)</label>
          <input
            type="number" min={0} step={0.01}
            value={revenue} onChange={(e) => setRevenue(e.target.value)}
            placeholder="0.00"
          />
        </div>
      )}
      <div className="modal-field">
        <label>Notes</label>
        <textarea
          value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any notes about this account…"
        />
      </div>
      <div className="modal-btns">
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
        <button className="btn-save" onClick={() => onSave({
          status,
          outcome: outcome || null,
          revenue: parseFloat(revenue) || 0,
          notes: notes.trim()
        })}>Save Update</button>
      </div>
    </Shell>
  );
}

interface NewBizModalProps {
  defaultAm: string;
  isAdmin: boolean;
  users: User[];
  onCancel: () => void;
  onSave: (data: {
    amName: string; companyName: string; contact: string; phone: string;
    email: string; source: NewBizSource; notes: string;
  }) => void;
}

export function NewBizModal({ defaultAm, isAdmin, users, onCancel, onSave }: NewBizModalProps) {
  const [amName, setAmName] = useState(defaultAm);
  const [companyName, setCompanyName] = useState('');
  const [contact, setContact] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [source, setSource] = useState<NewBizSource>('');
  const [notes, setNotes] = useState('');

  return (
    <Shell onClose={onCancel} maxWidth={500}>
      <div className="modal-title">Add <span>New Prospect</span></div>
      {isAdmin && (
        <div className="modal-field">
          <label>Account Manager</label>
          <select value={amName} onChange={(e) => setAmName(e.target.value)}>
            {users.map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
          </select>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="modal-field">
          <label>Company Name *</label>
          <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Prospect Ltd" />
        </div>
        <div className="modal-field">
          <label>Contact Name</label>
          <input type="text" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Jane Smith" />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="modal-field">
          <label>Phone</label>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+44 …" />
        </div>
        <div className="modal-field">
          <label>Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@prospect.com" />
        </div>
      </div>
      <div className="modal-field">
        <label>Source / How Found</label>
        <select value={source} onChange={(e) => setSource(e.target.value as NewBizSource)}>
          {SOURCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div className="modal-field">
        <label>Initial Notes</label>
        <textarea
          value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Initial research, why they're a good prospect…"
          style={{ minHeight: 60 }}
        />
      </div>
      <div className="modal-btns">
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
        <button className="btn-save" onClick={() => onSave({
          amName, companyName: companyName.trim(), contact: contact.trim(),
          phone: phone.trim(), email: email.trim(), source, notes: notes.trim()
        })}>Add Prospect</button>
      </div>
    </Shell>
  );
}

interface ReassignModalProps {
  currentAm: string;
  users: User[];
  onCancel: () => void;
  onSave: (toAm: string) => void;
}

export function ReassignModal({ currentAm, users, onCancel, onSave }: ReassignModalProps) {
  const [toAm, setToAm] = useState(currentAm);
  return (
    <Shell onClose={onCancel} maxWidth={420}>
      <div className="modal-title">Reassign <span>Account</span></div>
      <div className="modal-field">
        <label>Current Assignment</label>
        <input type="text" value={currentAm} readOnly style={{ opacity: 0.65, cursor: 'default' }} />
      </div>
      <div className="modal-field">
        <label>Reassign To</label>
        <select value={toAm} onChange={(e) => setToAm(e.target.value)}>
          {users.map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
        </select>
      </div>
      <div className="modal-btns">
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
        <button className="btn-save" onClick={() => onSave(toAm)}>Reassign</button>
      </div>
    </Shell>
  );
}
